// src/components/VoiceNotePlayer.js

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../style/VoiceNotePlayer.css";

const VoiceNotePlayer = ({
  audioSrcEn = "/voicenote_engl.mp3",
  audioSrcFr = "/voicenote_fren.mp3",
  autoPlay = false,
}) => {
  const { i18n } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  const audioSrc = i18n.language.startsWith("fr") ? audioSrcFr : audioSrcEn;

  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Reset player when language changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      setIsPlaying(false);
      setCurrentTime(0);
      audio.load();
    }
  }, [audioSrc]);

  useEffect(() => {
    if (autoPlay && audioRef.current) {
      const playAudio = () => {
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(() => {});
      };

      const timeout = setTimeout(playAudio, 300);
      return () => clearTimeout(timeout);
    }
  }, [autoPlay]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e) => {
    const progressBar = progressRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;
    const newTime = percentage * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="voice-note-player">
      <audio ref={audioRef} src={audioSrc} preload="metadata" />

      {/* Speaker Icon */}
      <div className="vnp-speaker-icon">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 5L6 9H2V15H6L11 19V5Z"
            stroke="#707070"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53"
            stroke="#707070"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Progress Section */}
      <div className="vnp-progress-section">
        {/* Progress Bar */}
        <div
          className="vnp-progress-container"
          ref={progressRef}
          onClick={handleProgressClick}
        >
          <div className="vnp-progress-track">
            <div
              className="vnp-progress-fill"
              style={{ width: `${progressPercentage}%` }}
            />
            <div
              className="vnp-progress-handle"
              style={{ left: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Time Display */}
        <div className="vnp-time-display">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Play/Pause Button */}
      <button className="vnp-play-button" onClick={togglePlayPause}>
        {isPlaying ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="6" y="4" width="4" height="16" fill="#4A7AFF" />
            <rect x="14" y="4" width="4" height="16" fill="#4A7AFF" />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 5V19L19 12L8 5Z" fill="#4A7AFF" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default VoiceNotePlayer;
